import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Dialog } from '@packages/webshop-design-system';
import { type FC, type ReactNode, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ConfirmModalProps {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  children?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  cancelLabel?: TransTextKeys;
  confirmLabel?: TransTextKeys;
  confirmStartAdornment?: ReactNode;
  interactOutside?: boolean;
  onCancel?: () => Promise<void> | void;
  onConfirm?: () => Promise<void> | void;
  isConfirmLoading?: boolean;
  showClose?: boolean;
  confirmVariant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'ghost'
    | 'ghostLink'
    | 'cta';
}

const ConfirmModal: FC<ConfirmModalProps> = ({
  open: controlledOpen,
  setOpen: controlledSetOpen,
  children,
  title,
  description,
  cancelLabel,
  confirmLabel,
  confirmStartAdornment,
  interactOutside = false,
  onCancel,
  onConfirm,
  showClose = true,
  isConfirmLoading,
  confirmVariant = 'primary',
}) => {
  const [unControlledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? unControlledOpen;
  const setOpen = controlledSetOpen ?? setUncontrolledOpen;

  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handleCancel = async () => {
    await onCancel?.();
    setOpen(false);
  };

  const handleConfirm = async () => {
    await onConfirm?.();
    setOpen(false);
  };

  return (
    <Dialog
      isOpen={open}
      onOpenChange={setOpen}
      trigger={children}
      isDismissable={interactOutside}
      isKeyboardDismissDisabled={!interactOutside}
      hideCloseButton={!showClose || !!isConfirmLoading}
      headerTitle={title}
      footer={
        <>
          {cancelLabel && (
            <Button
              data-testid="confirm-modal-cancel"
              variant="tertiary"
              className="w-auto rounded-lg laptop:min-w-[90px]"
              onClick={handleCancel}
              size={isLaptopOrBigger ? 'regular' : 'large'}
            >
              <TransText i18nKey={cancelLabel} />
            </Button>
          )}
          {confirmLabel && (
            <Button
              data-testid="confirm-modal-action"
              variant={confirmVariant}
              className="w-auto gap-2 rounded-lg laptop:min-w-[90px]"
              onClick={handleConfirm}
              isPending={isConfirmLoading}
              size={isLaptopOrBigger ? 'regular' : 'large'}
              startAdornment={confirmStartAdornment}
            >
              {/* TODO: Keep width static during isPending, rn Spinner is smaller than text and causes button shrinkage */}
              <TransText i18nKey={confirmLabel} />
            </Button>
          )}
        </>
      }
      className={{
        root: 'w-5/6 tablet:w-[480px]',
        header: 'border-none',
        footer: 'border-none',
      }}
      data-testid="confirm-modal"
    >
      <div className="text-dark">{description}</div>
    </Dialog>
  );
};

export default ConfirmModal;
